import "../../../css/CreateEvent.css";
import { Event, SavedFormQuestion, CustomTicketV2 } from "@markit/common.types";
import { ThemeStyle } from "../../../hooks/useTheme";
import PopupModalContainer from "../../Containers/PopupModalContainer";
import { Colors } from "../../../utils/colors";
import RectangleButton from "../../Buttons/RectangleButton";
import CustomCheckbox from "../../CustomCheckbox";
import { useState, useCallback, useMemo } from "react";
import AlertContainer from "../../Containers/AlertContainer";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";

interface CreateConditionalQuestionLogicModalProps {
  event: Event;
  updateEventSettings: (update: Partial<Event>) => void;
  selectedFormQuestion: SavedFormQuestion;
  setIsVisible: (isVisible: boolean) => void;
  theme: ThemeStyle;
}

const CreateConditionalQuestionLogicModal = (
  props: CreateConditionalQuestionLogicModalProps
) => {
  const {
    event,
    updateEventSettings,
    selectedFormQuestion,
    setIsVisible,
    theme,
  } = props;
  const { currentEventFormQuestions } = useSelector(getAccountState).account;

  // Create a local copy of the custom tickets for editing
  const [localCustomTickets, setLocalCustomTickets] = useState<
    CustomTicketV2[]
  >(event.customTickets);

  const [alertText, setAlertText] = useState<{
    header: string;
    subHeader: string;
  }>({ header: "", subHeader: "" });

  // Check if all tickets are selected for this form question
  const allTicketsSelected = useMemo(() => {
    return localCustomTickets.every((ticket) =>
      ticket.formQuestions.includes(selectedFormQuestion.id)
    );
  }, [localCustomTickets, selectedFormQuestion.id]);

  // Checks if no tickets are selected for the given form question
  const noTicketsSelected = useMemo(() => {
    return localCustomTickets.every(
      (ticket) => !ticket.formQuestions.includes(selectedFormQuestion.id)
    );
  }, [localCustomTickets, selectedFormQuestion.id]);

  // Handle select all/deselect all
  const handleSelectAll = useCallback(() => {
    if (allTicketsSelected) {
      // Deselect all tickets
      setLocalCustomTickets(
        localCustomTickets.map((ticket) => ({
          ...ticket,
          formQuestions: ticket.formQuestions.filter(
            (id) => id !== selectedFormQuestion.id
          ),
        }))
      );
    } else {
      // Select all tickets
      setLocalCustomTickets(
        localCustomTickets.map((ticket) => ({
          ...ticket,
          formQuestions: ticket.formQuestions.includes(selectedFormQuestion.id)
            ? ticket.formQuestions
            : [...ticket.formQuestions, selectedFormQuestion.id],
        }))
      );
    }
  }, [allTicketsSelected, localCustomTickets, selectedFormQuestion.id]);

  const handleChange = useCallback(
    (ticketId: string) => {
      // make sure to maintain order
      setLocalCustomTickets(
        localCustomTickets.map((ticket) =>
          ticket.id === ticketId
            ? {
                ...ticket,
                formQuestions: ticket.formQuestions.includes(
                  selectedFormQuestion.id
                )
                  ? ticket.formQuestions.filter(
                      (id) => id !== selectedFormQuestion.id
                    )
                  : [
                      ...currentEventFormQuestions
                        .filter(
                          (question) =>
                            ticket.formQuestions.includes(question.id) ||
                            question.id === selectedFormQuestion.id
                        )
                        .map((question) => question.id),
                    ],
              }
            : ticket
        )
      );
    },
    [currentEventFormQuestions, localCustomTickets, selectedFormQuestion.id]
  );

  const onSavePress = useCallback(() => {
    if (noTicketsSelected) {
      setAlertText({
        header: "Please select at least one ticket type.",
        subHeader: "You must have at least one ticket type selected.",
      });
      return;
    }
    updateEventSettings({
      customTickets: localCustomTickets,
    });
    setIsVisible(false);
  }, [
    noTicketsSelected,
    updateEventSettings,
    localCustomTickets,
    setIsVisible,
  ]);

  const CheckboxRow = useCallback(
    ({
      label,
      isChecked,
      onChange,
    }: {
      label: string;
      isChecked: boolean;
      onChange: () => void;
    }) => (
      <div className="AlignedRowSpaced" style={{ marginTop: 7 }}>
        <span
          className="bodyMedium"
          style={{
            ...theme.PrimaryText,
            fontSize: 13,
          }}
        >
          {label}
        </span>
        <CustomCheckbox checked={isChecked} onChange={onChange} />
      </div>
    ),
    [theme.PrimaryText]
  );

  return (
    <>
      <PopupModalContainer
        headerComp="Conditional Logic"
        subHeaderComp="Set this question to only appear when a certain ticket type is selected. Will show for all ticket types by default."
        valueComp={
          <div style={{ ...theme.SecondaryBG, marginTop: 6 }}>
            <span className="bodySubtextMedium">Show Question For</span>
            {/* All Ticket Types option */}
            <CheckboxRow
              label="All Ticket Types"
              isChecked={allTicketsSelected}
              onChange={handleSelectAll}
            />
            {localCustomTickets.map((ticketType) => (
              <CheckboxRow
                key={ticketType.id}
                label={ticketType.label}
                isChecked={ticketType.formQuestions.includes(
                  selectedFormQuestion.id
                )}
                onChange={() => handleChange(ticketType.id)}
              />
            ))}
          </div>
        }
        fixedBottomContent={
          <div className="AlignedRow" style={{ gap: 10, paddingInline: 14 }}>
            <RectangleButton
              buttonLabel={"Cancel"}
              onPress={() => setIsVisible(false)}
              altTextColor={theme.PrimaryText.color}
              altColor={theme.NewPrimaryBG.backgroundColor}
              containerStyles={{
                paddingBlock: 14,
                paddingInline: 12,
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            />
            <RectangleButton
              buttonLabel={"Save"}
              onPress={onSavePress}
              altTextColor={theme.ButtonText.color}
              altColor={theme.ButtonBG.backgroundColor}
              containerStyles={{ paddingBlock: 14, paddingInline: 12 }}
              disabled={false}
            />
          </div>
        }
        theme={theme}
        dividerTwo
        noExit
        closeModal={() => setIsVisible(false)}
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
        theme={theme}
      />
    </>
  );
};

export default CreateConditionalQuestionLogicModal;
